import React from 'react';
import validationConfig from '../../validation-config.json';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Router } from '@uhg-abyss/web/ui/Router';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { V1Table } from '@uhg-abyss/web/ui/Table';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { SharedDialog } from '../../common/SharedDialog';
import { PageSpinner } from '../../common/PageSpinner';
import { formatDisplayDateTime } from '../../common/dateTime/formatDisplayDateTime';
import { navigate } from '../../common/navigate';
import { useGlobalError } from '../../common/ErrorHandler';
import { useAuth } from '../../auth';
import {
  RuntimeSurveyQuestion,
  SurveyEntrySummary,
  SurveyResponseRecord,
  getSurveyEntry,
  getSurveyQuestionsBySurveyId,
  getSurveyResponseByCustomerIdentity,
  saveSurveyResponse,
} from './surveyApi';

const SurveyLayout = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
});

const Page = styled('section', {
  flex: '1 1 0',
  minWidth: 0,
  display: 'grid',
  gap: '1rem',
  boxSizing: 'border-box',
});

const ContextPanel = styled(Card, {
  width: '100%',
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  backgroundColor: '#f5f9fc',
  padding: '1rem 1.25rem',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.35rem',
});

const ContextLabel = styled(Text, {
  margin: 0,
  color: '#345570',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
});

const ContextPath = styled(Text, {
  margin: 0,
  color: '$web.semantic.color.text.content.primary',
  fontSize: '0.95rem',
  fontWeight: 600,
});

const ContextBody = styled(Text, {
  margin: 0,
  color: '$web.semantic.color.text.content.primary',
});

const DetailTable = styled('table', {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
});

const DetailThead = styled('thead', {
  backgroundColor: '#dceaf3',
});

const DetailTh = styled('th', {
  padding: '0.35rem 0.75rem',
  fontWeight: 700,
  color: '#345570',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #b8cad8',
});

const ThLabel = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
});

const DetailTd = styled('td', {
  padding: '0.35rem 0.75rem',
  fontWeight: 500,
  color: '$web.semantic.color.text.content.primary',
  fontSize: '0.88rem',
  wordBreak: 'break-word',
});

const SummaryPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  padding: '1.25rem',
  boxSizing: 'border-box',
});

const ActionRow = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
});

const VerticalRail = styled('div', {
  position: 'sticky',
  top: 0,
  height: 'calc(100vh - 72px - 36px)',
  width: '28px',
  flexShrink: 0,
  alignSelf: 'flex-start',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  boxSizing: 'border-box',
  backgroundColor: 'rgba(245, 249, 252, 0.95)',
  borderRight: '1px solid #dbe5ee',
});

const RailTrack = styled('div', {
  position: 'relative',
  width: '4px',
  flex: '1 1 0',
  backgroundColor: '#dbe5ee',
  borderRadius: '2px',
  overflow: 'visible',
});

const RailFill = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: '#345570',
  borderRadius: '2px',
  transition: 'height 0.35s ease',
  pointerEvents: 'none',
});

const RailDot = styled('button', {
  position: 'absolute',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  border: '2px solid #345570',
  cursor: 'pointer',
  padding: 0,
  transition: 'background-color 0.25s ease',
  flexShrink: 0,
});

const RailLabel = styled('span', {
  writingMode: 'vertical-lr',
  fontSize: '0.6rem',
  color: '#345570',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
});

const QuestionList = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const QuestionCard = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #dbe5ee',
  borderLeft: '4px solid #7ea1bd',
  padding: '1rem 1.1rem',
  background: 'linear-gradient(180deg, #f8fbfe 0%, #ffffff 42%)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.65rem',
});

const QuestionMeta = styled('div', {
  display: 'grid',
  gap: '0.6rem',
});

const QuestionPrompt = styled('div', {
  display: 'grid',
  gap: '0.4rem',
  padding: '0.5rem 0',
});

const TitleRow = styled('div', {
  display: 'flex',
  gap: '0.4rem',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
});

const HelpLink = styled('a', {
  fontSize: '0.875rem',
  color: '$web.semantic.color.text.link.primary',
  textDecoration: 'underline',
  '&:hover': { textDecoration: 'none' },
});

const TitleHelpLink = styled(HelpLink, {
  fontSize: '0.82rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const HiddenLabelChoiceList = styled('div', {
  '& [class*="radio-group-label"]': {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
});

const Field = styled('div', {
  display: 'grid',
  gap: '0.45rem',
});

const Label = styled('label', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

const Input = styled('input', {
  width: '100%',
  border: '1px solid #a0b5c8',
  borderRadius: '10px',
  padding: '0.7rem 0.8rem',
  boxSizing: 'border-box',
  backgroundColor: '#f9fbfd',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  '&:focus': {
    outline: '2px solid #7ea1bd',
    outlineOffset: '1px',
    borderColor: '#7ea1bd',
    backgroundColor: '#ffffff',
  },
});

const Textarea = styled('textarea', {
  width: '100%',
  minHeight: '112px',
  border: '1px solid #a0b5c8',
  borderRadius: '10px',
  padding: '0.7rem 0.8rem',
  boxSizing: 'border-box',
  resize: 'vertical',
  backgroundColor: '#f9fbfd',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  '&:focus': {
    outline: '2px solid #7ea1bd',
    outlineOffset: '1px',
    borderColor: '#7ea1bd',
    backgroundColor: '#ffffff',
  },
});

const ChoiceList = styled('div', {
  display: 'grid',
  gap: '0.55rem',
});

const ErrorSummary = styled(Card, {
  width: '100%',
  borderRadius: '14px',
  border: '1px solid #e2b3b3',
  backgroundColor: '#fff7f7',
  padding: '0.95rem 1rem',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.45rem',
});

const ErrorLinkList = styled('ul', {
  margin: 0,
  paddingLeft: '1.1rem',
  display: 'grid',
  gap: '0.35rem',
});

const PreviewList = styled('div', {
  display: 'grid',
  gap: '1.25rem',
});

const AttestationBox = styled('div', {
  borderRadius: '10px',
  border: '1px solid #bfdbfe',
  backgroundColor: '#eff6ff',
  padding: '1rem 1.1rem',
  boxSizing: 'border-box',
});

type SurveyAnswerState = {
  value: string;
  selectedOptionIds: number[];
};

type SurveyAnswerMap = Record<number, SurveyAnswerState>;

const HOME_INTERNAL_SEARCH_KEY = 'rfi.internalSearchContext';
const TYPE_TEXT = 1;
const TYPE_MULTILINE = 2;
const TYPE_CHECKBOX = 3;
const TYPE_RADIO = 4;
const TYPE_NUMERIC = 5;
const TYPE_ALPHANUMERIC = 6;
const TYPE_DATE = 7;

function parsePolicyValues(
  policyList: string | null | undefined,
  policyNumber: string | null | undefined
) {
  const values = (policyList ?? '')
    .split(',')
    .map((value) => {
      return value.trim();
    })
    .filter((value) => {
      return value.length > 0;
    });

  if (values.length > 0) {
    return values;
  }

  const fallback = (policyNumber ?? '').trim();
  return fallback ? [fallback] : [];
}

function getEntryIdFromPath() {
  const segments = window.location.pathname.split('/').filter(Boolean);
  return decodeURIComponent(segments[2] ?? '');
}

function isHistoryReadOnlyMode() {
  const params = new URLSearchParams(window.location.search);
  return params.get('fromHistory') === 'true';
}

function navigateTo(path: string) {
  navigate(path);
}

function readInternalSearchContext() {
  const raw = window.sessionStorage.getItem(HOME_INTERNAL_SEARCH_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as {
      searchKey?: string | null;
      quickFilter?: string;
      selectedCustomerNumber?: string;
      selectedPolicyNumber?: string;
      fromSurvey?: boolean;
      returnAction?: 'cancel' | 'save';
    };
  } catch {
    return null;
  }
}

function writeInternalSearchContext(args: {
  entry: SurveyEntrySummary;
  returnAction: 'cancel' | 'save';
}) {
  const previous = readInternalSearchContext();

  window.sessionStorage.setItem(
    HOME_INTERNAL_SEARCH_KEY,
    JSON.stringify({
      searchKey: previous?.searchKey ?? '',
      quickFilter: previous?.quickFilter ?? '',
      selectedCustomerNumber: args.entry.selectedCustomer.customerNumber,
      selectedPolicyNumber: args.entry.selectedCustomer.policyNumber,
      fromSurvey: true,
      returnAction: args.returnAction,
    })
  );
}

function normalizeQuestionTypeName(value: string | null | undefined) {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function matchesQuestionType(
  question: RuntimeSurveyQuestion,
  expectedId: number,
  aliases: string[]
) {
  if (question.questionTypeId === expectedId) {
    return true;
  }

  const normalizedTypeName = normalizeQuestionTypeName(question.questionType);
  return (
    normalizedTypeName.length > 0 &&
    aliases.some((alias) => {
      return normalizeQuestionTypeName(alias) === normalizedTypeName;
    })
  );
}

function isCheckboxQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_CHECKBOX, ['checkbox']);
}

function isRadioQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_RADIO, ['radio']);
}

function isSingleLineTextQuestion(question: RuntimeSurveyQuestion) {
  return (
    matchesQuestionType(question, TYPE_TEXT, ['textbox', 'text']) ||
    matchesQuestionType(question, TYPE_ALPHANUMERIC, [
      'alphanumeric',
      'alphanumric',
      'alphanumerictext',
    ]) ||
    matchesQuestionType(question, 9, [
      'alphanumeric',
      'alphanumric',
      'alphanumerictext',
    ])
  );
}

function isMultilineQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_MULTILINE, [
    'multiline',
    'textarea',
  ]);
}

function isNumericQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_NUMERIC, ['numeric', 'number']);
}

function isDateQuestion(question: RuntimeSurveyQuestion) {
  return (
    matchesQuestionType(question, TYPE_DATE, ['date']) ||
    matchesQuestionType(question, 11, ['date'])
  );
}

function isLengthValidatedQuestion(question: RuntimeSurveyQuestion) {
  return (
    isSingleLineTextQuestion(question) ||
    isMultilineQuestion(question) ||
    isNumericQuestion(question)
  );
}

function isOptionQuestion(question: RuntimeSurveyQuestion) {
  return isCheckboxQuestion(question) || isRadioQuestion(question);
}

function normalizeOptionText(value: string | null | undefined) {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function inferSelectedOptionIds(
  question: RuntimeSurveyQuestion,
  responseText: string | null | undefined
) {
  const normalizedResponse = normalizeOptionText(responseText);
  if (!normalizedResponse) {
    return [] as number[];
  }

  if (isRadioQuestion(question)) {
    const matchedOption = question.options.find((option) => {
      return normalizeOptionText(option.optionTitle) === normalizedResponse;
    });
    return matchedOption ? [matchedOption.questionOptionId] : [];
  }

  if (isCheckboxQuestion(question)) {
    const selectedIds: number[] = [];
    const normalizedParts = normalizedResponse
      .split(',')
      .map((part) => {
        return normalizeOptionText(part);
      })
      .filter(Boolean);

    for (const option of question.options) {
      const normalizedTitle = normalizeOptionText(option.optionTitle);
      if (normalizedParts.includes(normalizedTitle)) {
        selectedIds.push(option.questionOptionId);
      }
    }

    return selectedIds;
  }

  return [] as number[];
}

function hasAnswer(
  question: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined
) {
  if (!answer) {
    return false;
  }

  if (isOptionQuestion(question)) {
    return (
      answer.selectedOptionIds.length > 0 || answer.value.trim().length > 0
    );
  }

  return answer.value.trim().length > 0;
}

type ValidationConfigRule = {
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  min?: number;
  max?: number;
  errorMessage?: string;
};

const SURVEY_VALIDATION = {
  requiredMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['requiredMessage'] ?? 'This question is required.',
  numericMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['numericMessage'] ?? 'Enter a valid number.',
  minLengthMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['minLengthMessage'] ?? 'Enter at least {minLength} characters.',
  maxLengthMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['maxLengthMessage'] ?? 'Enter no more than {maxLength} characters.',
  draftFirstQuestionMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['draftFirstQuestionMessage'] ??
    'Answer the first question before saving a draft.',
  attestationRequiredMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['attestationRequiredMessage'] ??
    'Attestation is required before final submission.',
};

function getConfigRule(
  dbCode: string | null | undefined
): ValidationConfigRule | null {
  if (!dbCode) {
    return null;
  }

  const surveyPage = (validationConfig as Record<string, unknown>)[
    'SURVEY_PAGE'
  ] as Record<string, ValidationConfigRule> | undefined;
  return surveyPage?.[dbCode] ?? null;
}

function applyValidationConfig(
  question: RuntimeSurveyQuestion
): RuntimeSurveyQuestion {
  const rule = getConfigRule(question.surveyQuestionDbCode);
  if (!rule) {
    return question;
  }

  return {
    ...question,
    minLength: rule.minLength ?? question.minLength,
    maxLength: rule.maxLength ?? question.maxLength,
    required: rule.required ?? question.required,
    errorMessage: rule.errorMessage ?? question.errorMessage,
  };
}

function getValidationMessage(
  question: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined,
  prefillValue?: string
) {
  const q = applyValidationConfig(question);

  if (q.required && !hasAnswer(q, answer)) {
    return q.errorMessage ?? SURVEY_VALIDATION.requiredMessage;
  }

  if (!answer || !answer.value.trim()) {
    return null;
  }

  const value = answer.value.trim();

  if (prefillValue && value === prefillValue.trim()) {
    return null;
  }

  if (isNumericQuestion(q) && Number.isNaN(Number(value))) {
    return q.errorMessage ?? SURVEY_VALIDATION.numericMessage;
  }

  if (
    isLengthValidatedQuestion(q) &&
    q.minLength != null &&
    value.length < q.minLength
  ) {
    return (
      q.errorMessage ??
      SURVEY_VALIDATION.minLengthMessage.replace(
        '{minLength}',
        String(q.minLength)
      )
    );
  }

  if (
    isLengthValidatedQuestion(q) &&
    q.maxLength != null &&
    value.length > q.maxLength
  ) {
    return SURVEY_VALIDATION.maxLengthMessage.replace(
      '{maxLength}',
      String(q.maxLength)
    );
  }

  return null;
}

function getQuestionAnchorId(questionId: number) {
  return `survey-question-${questionId}`;
}

function getQuestionInputId(
  question: RuntimeSurveyQuestion,
  visibleIndex: number
) {
  return `SURVEY_PAGE.${question.questionTypeId}_${visibleIndex + 1}`;
}

function getPreferredFirstQuestionAnswerValue(selectedCustomer: {
  employerGroupName: string;
  customerNumber: string;
}) {
  const employerGroupName = selectedCustomer.employerGroupName.trim();
  if (employerGroupName.length > 0) {
    return employerGroupName;
  }

  return selectedCustomer.customerNumber.trim();
}

function buildHydratedAnswers(
  questions: RuntimeSurveyQuestion[],
  savedResponse: SurveyResponseRecord | null,
  selectedCustomer: {
    employerGroupName: string;
    customerNumber: string;
  }
) {
  const nextAnswers: SurveyAnswerMap = {};
  const questionById = new Map<number, RuntimeSurveyQuestion>();

  for (const question of questions) {
    questionById.set(question.questionId, question);
  }

  for (const detail of savedResponse?.surveyResponseDetailsDTOList ?? []) {
    const question = questionById.get(detail.questionId);
    const selectedOptionIds =
      detail.surveyResponseDetailsOptionsDTOList.length > 0
        ? detail.surveyResponseDetailsOptionsDTOList.map((option) => {
            return option.questionOptionId;
          })
        : question
          ? inferSelectedOptionIds(question, detail.response)
          : [];

    nextAnswers[detail.questionId] = {
      value: detail.response ?? '',
      selectedOptionIds,
    };
  }

  const firstQuestion = questions[0];
  const preferredFirstAnswerValue =
    getPreferredFirstQuestionAnswerValue(selectedCustomer);
  if (
    firstQuestion &&
    !isOptionQuestion(firstQuestion) &&
    preferredFirstAnswerValue.length > 0
  ) {
    const existingFirstAnswer = nextAnswers[firstQuestion.questionId];
    const shouldReplaceWithPrefill = !hasAnswer(
      firstQuestion,
      existingFirstAnswer
    );

    if (shouldReplaceWithPrefill) {
      nextAnswers[firstQuestion.questionId] = {
        value: preferredFirstAnswerValue,
        selectedOptionIds: [],
      };
    }
  }

  return nextAnswers;
}

function computeVisibleQuestionIds(
  questions: RuntimeSurveyQuestion[],
  answers: SurveyAnswerMap
) {
  if (questions.length === 0) {
    return [] as number[];
  }

  const indexByQuestionId = new Map<number, number>();
  for (let index = 0; index < questions.length; index += 1) {
    indexByQuestionId.set(questions[index]!.questionId, index);
  }

  const visibleQuestionIds: number[] = [];
  const visited = new Set<number>();
  let currentIndex = 0;

  while (
    currentIndex >= 0 &&
    currentIndex < questions.length &&
    !visited.has(currentIndex)
  ) {
    visited.add(currentIndex);
    const question = questions[currentIndex]!;
    visibleQuestionIds.push(question.questionId);

    const answer = answers[question.questionId];
    if (!hasAnswer(question, answer)) {
      break;
    }

    if (isRadioQuestion(question)) {
      const selectedOptionId = answer.selectedOptionIds[0];
      const matchingRule = question.branchRules.find((rule) => {
        return rule.questionOptionId === selectedOptionId;
      });
      if (matchingRule) {
        const jumpIndex = indexByQuestionId.get(matchingRule.jumpToQuestionId);
        currentIndex = jumpIndex ?? currentIndex + 1;
        continue;
      }
    }

    currentIndex += 1;
  }

  return visibleQuestionIds;
}

function computeProgressState(
  questions: RuntimeSurveyQuestion[],
  answers: SurveyAnswerMap
) {
  if (questions.length === 0) {
    return {
      totalQuestionCount: 0,
      completedCount: 0,
      progressPct: 0,
      completedQuestionIds: new Set<number>(),
    };
  }

  const indexByQuestionId = new Map<number, number>();
  for (let index = 0; index < questions.length; index += 1) {
    indexByQuestionId.set(questions[index]!.questionId, index);
  }

  const skippedQuestionIds = new Set<number>();
  const completedQuestionIds = new Set<number>();
  const visited = new Set<number>();
  let currentIndex = 0;

  while (
    currentIndex >= 0 &&
    currentIndex < questions.length &&
    !visited.has(currentIndex)
  ) {
    visited.add(currentIndex);
    const question = questions[currentIndex]!;
    const answer = answers[question.questionId];
    const isAnswered = hasAnswer(question, answer);

    if (isAnswered) {
      completedQuestionIds.add(question.questionId);
    }

    if (isRadioQuestion(question) && isAnswered) {
      const selectedOptionId = answer.selectedOptionIds[0];
      const matchingRule = question.branchRules.find((rule) => {
        return rule.questionOptionId === selectedOptionId;
      });
      if (matchingRule) {
        const jumpIndex =
          indexByQuestionId.get(matchingRule.jumpToQuestionId) ??
          currentIndex + 1;

        if (jumpIndex > currentIndex + 1) {
          for (
            let skippedIndex = currentIndex + 1;
            skippedIndex < jumpIndex;
            skippedIndex += 1
          ) {
            const skippedQuestionId = questions[skippedIndex]!.questionId;
            skippedQuestionIds.add(skippedQuestionId);
            completedQuestionIds.add(skippedQuestionId);
          }
        }

        currentIndex = jumpIndex;
        continue;
      }
    }

    if (!isAnswered) {
      break;
    }

    currentIndex += 1;
  }

  const totalQuestionCount = questions.length;
  const completedCount = Math.min(
    totalQuestionCount,
    completedQuestionIds.size
  );
  const progressPct =
    totalQuestionCount > 0
      ? Math.round((completedCount / totalQuestionCount) * 100)
      : 0;

  return {
    totalQuestionCount,
    completedCount,
    progressPct,
    completedQuestionIds,
  };
}

function pruneAnswersToVisible(
  answers: SurveyAnswerMap,
  visibleQuestionIds: number[]
) {
  const visibleSet = new Set(visibleQuestionIds);
  const nextAnswers: SurveyAnswerMap = {};

  for (const [questionId, answer] of Object.entries(answers)) {
    const numericQuestionId = Number(questionId);
    if (visibleSet.has(numericQuestionId)) {
      nextAnswers[numericQuestionId] = answer;
    }
  }

  return nextAnswers;
}

function resolveAnswersForValidation(args: {
  questions: RuntimeSurveyQuestion[];
  answers: SurveyAnswerMap;
}) {
  let nextAnswers: SurveyAnswerMap = { ...args.answers };

  for (const question of args.questions) {
    if (!isOptionQuestion(question)) {
      continue;
    }

    const currentAnswer = nextAnswers[question.questionId];
    if (!currentAnswer) {
      continue;
    }

    if (
      currentAnswer.selectedOptionIds.length === 0 &&
      currentAnswer.value.trim().length > 0
    ) {
      const inferredOptionIds = inferSelectedOptionIds(
        question,
        currentAnswer.value
      );
      if (inferredOptionIds.length > 0) {
        nextAnswers[question.questionId] = {
          ...currentAnswer,
          selectedOptionIds: inferredOptionIds,
        };
      }
    }
  }

  const nextVisibleQuestionIds = computeVisibleQuestionIds(
    args.questions,
    nextAnswers
  );
  const prunedAnswers = pruneAnswersToVisible(
    nextAnswers,
    nextVisibleQuestionIds
  );

  return {
    answers: prunedAnswers,
    visibleQuestionIds: nextVisibleQuestionIds,
    visibleQuestions: args.questions.filter((question) => {
      return nextVisibleQuestionIds.includes(question.questionId);
    }),
  };
}

function getAnswerText(
  question: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined
) {
  if (!answer || !hasAnswer(question, answer)) {
    return 'Not answered';
  }

  if (isOptionQuestion(question)) {
    const selectedLabels = question.options
      .filter((option) => {
        return answer.selectedOptionIds.includes(option.questionOptionId);
      })
      .map((option) => {
        return option.optionTitle;
      });
    return selectedLabels.length > 0
      ? selectedLabels.join(', ')
      : answer.value.trim();
  }

  return answer.value.trim();
}

function getOptionTitle(
  question: RuntimeSurveyQuestion,
  questionOptionId: number
) {
  return (
    question.options.find((option) => {
      return option.questionOptionId === questionOptionId;
    })?.optionTitle ?? ''
  );
}

function buildSavePayload(args: {
  entry: SurveyEntrySummary;
  sessionDisplayName: string;
  persistedUserId: number;
  questions: RuntimeSurveyQuestion[];
  answers: SurveyAnswerMap;
  visibleQuestionIds: number[];
  savedResponse: SurveyResponseRecord | null;
  isFinalSubmit: boolean;
}) {
  const existingDetailsByQuestionId = new Map<
    number,
    SurveyResponseRecord['surveyResponseDetailsDTOList'][number]
  >();
  for (const detail of args.savedResponse?.surveyResponseDetailsDTOList ?? []) {
    existingDetailsByQuestionId.set(detail.questionId, detail);
  }

  const visibleSet = new Set(args.visibleQuestionIds);
  const details = args.questions
    .filter((question) => {
      return (
        visibleSet.has(question.questionId) &&
        hasAnswer(question, args.answers[question.questionId])
      );
    })
    .map((question) => {
      const answer = args.answers[question.questionId]!;
      const existingDetail = existingDetailsByQuestionId.get(
        question.questionId
      );
      const existingOptionsByQuestionOptionId = new Map<
        number,
        string | null
      >();

      for (const option of existingDetail?.surveyResponseDetailsOptionsDTOList ??
        []) {
        existingOptionsByQuestionOptionId.set(
          option.questionOptionId,
          option.rdOptionsId ?? null
        );
      }

      return {
        responseDetailsId: existingDetail?.responseDetailsId ?? null,
        surveyResponseId: args.savedResponse?.surveyResponseId ?? null,
        questionId: question.questionId,
        response: isOptionQuestion(question)
          ? getAnswerText(question, answer)
          : answer.value.trim(),
        surveyResponseDetailsOptionsDTOList: answer.selectedOptionIds.map(
          (questionOptionId) => {
            return {
              rdOptionsId:
                existingOptionsByQuestionOptionId.get(questionOptionId) ?? null,
              responseDetailsId: existingDetail?.responseDetailsId ?? null,
              questionOptionId,
            };
          }
        ),
      };
    });

  return {
    surveyResponseId: args.savedResponse?.surveyResponseId ?? null,
    surveyId: args.entry.activeSurvey.surveyId,
    userName: args.sessionDisplayName,
    userId: args.persistedUserId,
    status: args.isFinalSubmit ? 'Complete' : 'In Process',
    customerNumber: args.entry.selectedCustomer.customerNumber,
    fundingType: args.entry.selectedCustomer.fundingType,
    reportingYear: Number(args.entry.selectedCustomer.surveyYear),
    attested: args.isFinalSubmit ? 1 : 0,
    startDate: args.savedResponse?.startDate ?? new Date().toISOString(),
    completedDate: args.isFinalSubmit ? new Date().toISOString() : null,
    surveyResponseDetailsDTOList: details,
  } satisfies SurveyResponseRecord;
}

function buildSaveSuccessDescription(isFinalSubmit: boolean) {
  return isFinalSubmit
    ? 'Your survey response has been submitted successfully. Select OK to return to the Home screen.'
    : 'Your survey draft has been saved successfully. Select OK to return to the Home screen.';
}

export const Survey = () => {
  const { session } = useAuth();
  const { setError: setGlobalError } = useGlobalError();
  const entryId = React.useMemo(() => {
    return getEntryIdFromPath();
  }, []);
  const isReadOnlyHistoryMode = React.useMemo(() => {
    return isHistoryReadOnlyMode();
  }, []);
  const [entry, setEntry] = React.useState<SurveyEntrySummary | null>(null);
  const [questions, setQuestions] = React.useState<RuntimeSurveyQuestion[]>([]);
  const [savedResponse, setSavedResponse] =
    React.useState<SurveyResponseRecord | null>(null);
  const [answers, setAnswers] = React.useState<SurveyAnswerMap>({});
  const [touchedQuestionIds, setTouchedQuestionIds] = React.useState<
    Record<number, boolean>
  >({});
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const [showSaveSuccessDialog, setShowSaveSuccessDialog] =
    React.useState(false);
  const [attested, setAttested] = React.useState(false);
  const [saveSuccessResponse, setSaveSuccessResponse] =
    React.useState<SurveyResponseRecord | null>(null);
  const [saveSuccessWasFinalSubmit, setSaveSuccessWasFinalSubmit] =
    React.useState(false);
  const [validationSummary, setValidationSummary] = React.useState<
    Array<{ questionId: number; questionTitle: string; message: string }>
  >([]);

  const visibleQuestionIds = React.useMemo(() => {
    return computeVisibleQuestionIds(questions, answers);
  }, [answers, questions]);

  const visibleQuestions = React.useMemo(() => {
    return questions.filter((question) => {
      return visibleQuestionIds.includes(question.questionId);
    });
  }, [questions, visibleQuestionIds]);

  const visibleQuestionIdSet = React.useMemo(() => {
    return new Set(visibleQuestionIds);
  }, [visibleQuestionIds]);

  const requiredVisibleCount = React.useMemo(() => {
    return visibleQuestions.filter((question) => {
      return question.required;
    }).length;
  }, [visibleQuestions]);

  const requiredAnsweredCount = React.useMemo(() => {
    return visibleQuestions.filter((question) => {
      return (
        question.required && hasAnswer(question, answers[question.questionId])
      );
    }).length;
  }, [answers, visibleQuestions]);

  const progressState = React.useMemo(() => {
    return computeProgressState(questions, answers);
  }, [answers, questions]);

  const showBackendError = React.useCallback(
    (title: string, fallbackMessage: string, nextError: unknown) => {
      const message =
        nextError instanceof Error ? nextError.message : fallbackMessage;
      setGlobalError({
        title,
        message,
      });
    },
    [setGlobalError]
  );

  React.useEffect(() => {
    let isMounted = true;

    const loadEntry = async () => {
      if (!session?.token) {
        if (isMounted) {
          setGlobalError({
            title: 'Session expired',
            message: 'No authenticated session found. Please sign in again.',
            isAuthError: true,
          });
          setIsLoading(false);
        }
        return;
      }

      if (!entryId) {
        if (isMounted) {
          setGlobalError({
            title: 'Invalid URL',
            message: 'Invalid survey entry URL.',
          });
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const nextEntry = await getSurveyEntry({
          entryId,
        });

        const [nextQuestions, nextSavedResponse] = await Promise.all([
          getSurveyQuestionsBySurveyId({
            surveyId: nextEntry.activeSurvey.surveyId,
          }),
          getSurveyResponseByCustomerIdentity({
            customerNumber: nextEntry.selectedCustomer.customerNumber,
            fundingType: nextEntry.selectedCustomer.fundingType,
            reportingYear: Number(nextEntry.selectedCustomer.surveyYear),
            surveyId: nextEntry.activeSurvey.surveyId,
          }),
        ]);

        if (isMounted) {
          const hydratedAnswers = buildHydratedAnswers(
            nextQuestions,
            nextSavedResponse,
            {
              employerGroupName: nextEntry.selectedCustomer.employerGroupName,
              customerNumber: nextEntry.selectedCustomer.customerNumber,
            }
          );
          const initialVisibleIds = computeVisibleQuestionIds(
            nextQuestions,
            hydratedAnswers
          );
          setEntry(nextEntry);
          setQuestions(nextQuestions);
          setSavedResponse(nextSavedResponse);
          setAnswers(pruneAnswersToVisible(hydratedAnswers, initialVisibleIds));
          setTouchedQuestionIds({});
          setAttested(false);
          setValidationSummary([]);
        }
      } catch (nextError) {
        if (isMounted) {
          setEntry(null);
          setQuestions([]);
          setSavedResponse(null);
          setAnswers({});
          setError(null);
          showBackendError(
            'Unable to load survey entry session',
            'Unable to load survey entry session.',
            nextError
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadEntry();

    return () => {
      isMounted = false;
    };
  }, [entryId, session?.token, showBackendError]);

  const returnToHome = React.useCallback(
    (returnAction: 'cancel' | 'save') => {
      if (!entry) {
        return;
      }

      writeInternalSearchContext({ entry, returnAction });

      setActionMessage(
        returnAction === 'save'
          ? 'Survey responses saved. Returning to Home...'
          : 'Survey cancelled. Returning to Home...'
      );

      window.setTimeout(() => {
        navigateTo('/home');
      }, 120);
    },
    [entry]
  );

  const returnToHistory = React.useCallback(() => {
    navigateTo('/historicdata');
  }, []);

  const setQuestionTouched = React.useCallback((questionId: number) => {
    setTouchedQuestionIds((prev) => {
      return {
        ...prev,
        [questionId]: true,
      };
    });
  }, []);

  const focusQuestionSection = React.useCallback((questionId: number) => {
    const questionCard = window.document.getElementById(
      getQuestionAnchorId(questionId)
    );
    if (!questionCard) {
      return;
    }

    questionCard.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const focusTarget = questionCard.querySelector<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusTarget) {
      window.setTimeout(() => {
        focusTarget.focus();
      }, 120);
    }
  }, []);

  const updateAnswer = React.useCallback(
    (questionId: number, nextAnswer: SurveyAnswerState) => {
      setAnswers((prev) => {
        const mergedAnswers = {
          ...prev,
          [questionId]: nextAnswer,
        };
        const nextVisibleQuestionIds = computeVisibleQuestionIds(
          questions,
          mergedAnswers
        );
        return pruneAnswersToVisible(mergedAnswers, nextVisibleQuestionIds);
      });
    },
    [questions]
  );

  const validateVisibleQuestions = React.useCallback(() => {
    const resolved = resolveAnswersForValidation({
      questions,
      answers,
    });

    if (resolved.answers !== answers) {
      setAnswers(resolved.answers);
    }

    const firstQuestionId = questions[0]?.questionId;
    const firstQuestionPrefillValue = entry?.selectedCustomer
      ? getPreferredFirstQuestionAnswerValue(entry.selectedCustomer)
      : undefined;

    const issues = resolved.visibleQuestions
      .map((question) => {
        const answer = resolved.answers[question.questionId];
        const prefillValue =
          question.questionId === firstQuestionId && !isOptionQuestion(question)
            ? firstQuestionPrefillValue
            : undefined;
        const message = getValidationMessage(question, answer, prefillValue);
        return message
          ? {
              questionId: question.questionId,
              questionTitle: question.questionTitle,
              message,
            }
          : null;
      })
      .filter(
        (
          issue
        ): issue is {
          questionId: number;
          questionTitle: string;
          message: string;
        } => {
          return issue !== null;
        }
      );

    setValidationSummary(issues);
    if (issues.length > 0) {
      setTouchedQuestionIds((prev) => {
        const nextTouched = { ...prev };
        for (const issue of issues) {
          nextTouched[issue.questionId] = true;
        }
        return nextTouched;
      });

      focusQuestionSection(issues[0]!.questionId);
    }
    return issues;
  }, [answers, entry, focusQuestionSection, questions]);

  const handleSave = React.useCallback(
    async (isFinalSubmit: boolean) => {
      if (!entry || !session?.token) {
        setGlobalError({
          title: 'Session expired',
          message: 'No authenticated session found. Please sign in again.',
          isAuthError: true,
        });
        return;
      }

      const persistedUserId =
        session.persisted?.user_id ?? savedResponse?.userId ?? null;
      if (!persistedUserId) {
        setGlobalError({
          title: 'Session error',
          message:
            'Authenticated user context is incomplete. Please sign in again.',
          isAuthError: true,
        });
        return;
      }

      const resolved = resolveAnswersForValidation({
        questions,
        answers,
      });

      if (resolved.answers !== answers) {
        setAnswers(resolved.answers);
      }

      if (!isFinalSubmit) {
        const firstQuestion = questions[0];
        if (
          firstQuestion &&
          !hasAnswer(firstQuestion, resolved.answers[firstQuestion.questionId])
        ) {
          setValidationSummary([
            {
              questionId: firstQuestion.questionId,
              questionTitle: firstQuestion.questionTitle,
              message: SURVEY_VALIDATION.draftFirstQuestionMessage,
            },
          ]);
          setQuestionTouched(firstQuestion.questionId);
          focusQuestionSection(firstQuestion.questionId);
          return;
        }
      }

      if (isFinalSubmit) {
        const issues = validateVisibleQuestions();
        if (issues.length > 0 || !attested) {
          if (!attested) {
            setError(SURVEY_VALIDATION.attestationRequiredMessage);
          }
          return;
        }
      }

      setIsSaving(true);
      setError(null);

      try {
        const payload = buildSavePayload({
          entry,
          sessionDisplayName: session.displayName,
          persistedUserId,
          questions,
          answers: resolved.answers,
          visibleQuestionIds: resolved.visibleQuestionIds,
          savedResponse,
          isFinalSubmit,
        });

        const persistedResponse = await saveSurveyResponse({
          payload,
        });

        setSavedResponse(persistedResponse);
        setShowPreview(false);
        setAttested(false);
        setSaveSuccessResponse(persistedResponse);
        setSaveSuccessWasFinalSubmit(isFinalSubmit);
        setShowSaveSuccessDialog(true);
      } catch (nextError) {
        setError(null);
        showBackendError(
          'Unable to save survey response',
          'Unable to save survey response.',
          nextError
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      answers,
      attested,
      entry,
      focusQuestionSection,
      questions,
      returnToHome,
      savedResponse,
      session,
      setQuestionTouched,
      showBackendError,
      validateVisibleQuestions,
      visibleQuestionIds,
    ]
  );

  const openPreview = React.useCallback(() => {
    const issues = validateVisibleQuestions();
    if (issues.length > 0) {
      setShowPreview(false);
      return;
    }

    setError(null);
    setShowPreview(true);
  }, [validateVisibleQuestions]);

  const policyValues = entry
    ? parsePolicyValues(
        entry.selectedCustomer.policyList,
        entry.selectedCustomer.policyNumber
      )
    : [];
  const hasPolicyOverflowRow = policyValues.length > 6;
  const policyDisplayValue =
    policyValues.length > 0 ? policyValues.join(', ') : '—';
  const detailColumnCount = hasPolicyOverflowRow ? 5 : 6;

  return (
    <React.Fragment>
      <Router.MetaTags title="Survey" />
      <SurveyLayout>
        {!isLoading && entry !== null && questions.length > 0 ? (
          <VerticalRail
            role="progressbar"
            aria-valuenow={progressState.progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Survey progress"
          >
            <RailLabel>
              {progressState.completedCount}/{progressState.totalQuestionCount}
            </RailLabel>
            <RailTrack>
              <RailFill style={{ height: `${progressState.progressPct}%` }} />
              {questions.map((question, index) => {
                const isVisible = visibleQuestionIdSet.has(question.questionId);
                const isCompleted = progressState.completedQuestionIds.has(
                  question.questionId
                );
                const topPct =
                  questions.length > 1
                    ? (index / (questions.length - 1)) * 100
                    : 50;
                return (
                  <RailDot
                    key={question.questionId}
                    type="button"
                    aria-label={`Question ${index + 1}${isCompleted ? ', completed' : ''}${isVisible ? '' : ', not currently visible'}`}
                    disabled={!isVisible}
                    style={{
                      top: `${topPct}%`,
                      backgroundColor: isCompleted ? '#345570' : '#ffffff',
                    }}
                    onClick={() => {
                      focusQuestionSection(question.questionId);
                    }}
                  />
                );
              })}
            </RailTrack>
          </VerticalRail>
        ) : null}
        <Page>
          <ContextPanel>
            <ContextLabel>Current workspace</ContextLabel>
            <ContextPath>Home / Survey workspace</ContextPath>
            {entry ? (
              <DetailTable>
                <DetailThead>
                  <tr>
                    <DetailTh>Employer Group Name</DetailTh>
                    <DetailTh>EIN</DetailTh>
                    <DetailTh>Client Type</DetailTh>
                    <DetailTh>
                      <ThLabel>
                        Funding Type
                        <Tooltip
                          content={
                            <React.Fragment>
                              <div>
                                <strong>ASO:</strong> Administrative Services
                                Only
                              </div>
                              <div>
                                <strong>FI:</strong> Fully Insured
                              </div>
                              <div>
                                <strong>LF:</strong> Level Funded
                              </div>
                              <div>
                                <strong>MP:</strong> Minimum Premium
                              </div>
                              <div>
                                <strong>MMP:</strong> Modified Minimum Premium
                              </div>
                            </React.Fragment>
                          }
                          position="top"
                          variant="gray"
                          delayDuration={200}
                        >
                          <span
                            aria-label="Funding type information"
                            style={{
                              cursor: 'help',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '1.1rem',
                              height: '1.1rem',
                              borderRadius: '50%',
                              backgroundColor: '#345570',
                              color: '#ffffff',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              lineHeight: 1,
                              textTransform: 'none',
                              letterSpacing: 0,
                              flexShrink: 0,
                            }}
                          >
                            ?
                          </span>
                        </Tooltip>
                      </ThLabel>
                    </DetailTh>
                    <DetailTh>Reference Year</DetailTh>
                    {!hasPolicyOverflowRow ? (
                      <DetailTh>Policy List</DetailTh>
                    ) : null}
                  </tr>
                </DetailThead>
                <tbody>
                  <tr>
                    <DetailTd>
                      {entry.selectedCustomer.employerGroupName || '—'}
                    </DetailTd>
                    <DetailTd>
                      {entry.selectedCustomer.employerGroupEIN || '—'}
                    </DetailTd>
                    <DetailTd>
                      {session?.authType === 'INTERNAL'
                        ? 'Internal'
                        : 'External'}
                    </DetailTd>
                    <DetailTd>
                      {entry.selectedCustomer.fundingType || '—'}
                    </DetailTd>
                    <DetailTd>
                      {entry.selectedCustomer.surveyYear || '—'}
                    </DetailTd>
                    {!hasPolicyOverflowRow ? (
                      <DetailTd>{policyDisplayValue}</DetailTd>
                    ) : null}
                  </tr>
                  {hasPolicyOverflowRow ? (
                    <tr>
                      <DetailTd colSpan={detailColumnCount}>
                        <strong>Policy Numbers:</strong> {policyDisplayValue}
                      </DetailTd>
                    </tr>
                  ) : null}
                </tbody>
              </DetailTable>
            ) : (
              <ContextBody>
                Loading selected employer group context...
              </ContextBody>
            )}
          </ContextPanel>

          <Heading
            level={3}
            size="md"
            color="$web.semantic.color.text.content.primary"
            textAlign="left"
          >
            {entry
              ? `${entry.selectedCustomer.surveyYear} Survey - ${entry.selectedCustomer.employerGroupName || entry.selectedCustomer.customerNumber}`
              : 'Survey workspace'}
          </Heading>

          {validationSummary.length > 0 ? (
            <ErrorSummary>
              <Text color="$web.semantic.color.text.content.error">
                Please resolve the following issues before continuing.
              </Text>
              <ErrorLinkList>
                {validationSummary.map((issue) => {
                  return (
                    <li key={issue.questionId}>
                      <a href={`#${getQuestionAnchorId(issue.questionId)}`}>
                        {issue.questionTitle}
                      </a>
                      : {issue.message}
                    </li>
                  );
                })}
              </ErrorLinkList>
            </ErrorSummary>
          ) : null}

          {isLoading ||
          (!isLoading && entry !== null && visibleQuestions.length === 0) ? (
            <SummaryPanel>
              {isLoading ? <Text>Loading survey entry session...</Text> : null}
              {!isLoading && entry && visibleQuestions.length === 0 ? (
                <React.Fragment>
                  <Text>
                    No active survey questions are assigned to this survey.
                  </Text>
                  <ActionRow>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (isReadOnlyHistoryMode) {
                          returnToHistory();
                          return;
                        }
                        returnToHome('cancel');
                      }}
                      isDisabled={isSaving}
                    >
                      {isReadOnlyHistoryMode
                        ? 'Back to History'
                        : 'Return Home'}
                    </Button>
                  </ActionRow>
                </React.Fragment>
              ) : null}
            </SummaryPanel>
          ) : null}

          {!isLoading && entry !== null && visibleQuestions.length > 0 ? (
            <QuestionList>
              {visibleQuestions.map((question, index) => {
                const answer = answers[question.questionId] ?? {
                  value: '',
                  selectedOptionIds: [],
                };
                const prefillValue =
                  index === 0 &&
                  !isOptionQuestion(question) &&
                  entry?.selectedCustomer
                    ? getPreferredFirstQuestionAnswerValue(
                        entry.selectedCustomer
                      )
                    : undefined;
                const validationMessage = touchedQuestionIds[
                  question.questionId
                ]
                  ? getValidationMessage(question, answer, prefillValue)
                  : null;

                return (
                  <QuestionCard
                    key={question.questionId}
                    id={getQuestionAnchorId(question.questionId)}
                    style={{
                      borderLeftColor: question.required
                        ? '#2d607f'
                        : '#b8cad8',
                      borderLeftWidth: question.required ? '6px' : '4px',
                    }}
                  >
                    <QuestionMeta>
                      <QuestionPrompt>
                        <TitleRow>
                          <Heading
                            level={4}
                            size="sm"
                            color="$web.semantic.color.text.content.primary"
                            textAlign="left"
                          >
                            {question.questionTitle}
                          </Heading>
                          {question.helpLink && question.helpLinkLabel ? (
                            <TitleHelpLink
                              href={question.helpLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {question.helpLinkLabel}
                            </TitleHelpLink>
                          ) : null}
                        </TitleRow>
                        {question.helpText ? (
                          <Text
                            color="$web.semantic.color.text.content.secondary"
                            fontSize="0.9rem"
                          >
                            {question.helpText}
                          </Text>
                        ) : null}
                      </QuestionPrompt>
                    </QuestionMeta>

                    {isSingleLineTextQuestion(question) ? (
                      <Field>
                        <Label htmlFor={getQuestionInputId(question, index)}>
                          Answer
                        </Label>
                        <Input
                          id={getQuestionInputId(question, index)}
                          type="text"
                          value={answer.value}
                          disabled={isSaving || isReadOnlyHistoryMode}
                          onChange={(event) => {
                            updateAnswer(question.questionId, {
                              value: event.target.value,
                              selectedOptionIds: [],
                            });
                          }}
                          onBlur={() => {
                            setQuestionTouched(question.questionId);
                          }}
                        />
                      </Field>
                    ) : null}

                    {isMultilineQuestion(question) ? (
                      <Field>
                        <Label htmlFor={getQuestionInputId(question, index)}>
                          Answer
                        </Label>
                        <Textarea
                          id={getQuestionInputId(question, index)}
                          value={answer.value}
                          disabled={isSaving || isReadOnlyHistoryMode}
                          onChange={(event) => {
                            updateAnswer(question.questionId, {
                              value: event.target.value,
                              selectedOptionIds: [],
                            });
                          }}
                          onBlur={() => {
                            setQuestionTouched(question.questionId);
                          }}
                        />
                      </Field>
                    ) : null}

                    {isNumericQuestion(question) ? (
                      <Field>
                        <Label htmlFor={getQuestionInputId(question, index)}>
                          Answer
                        </Label>
                        <Input
                          id={getQuestionInputId(question, index)}
                          type="number"
                          value={answer.value}
                          disabled={isSaving || isReadOnlyHistoryMode}
                          onChange={(event) => {
                            updateAnswer(question.questionId, {
                              value: event.target.value,
                              selectedOptionIds: [],
                            });
                          }}
                          onBlur={() => {
                            setQuestionTouched(question.questionId);
                          }}
                        />
                      </Field>
                    ) : null}

                    {isDateQuestion(question) ? (
                      <Field>
                        <Label htmlFor={getQuestionInputId(question, index)}>
                          Answer
                        </Label>
                        <Input
                          id={getQuestionInputId(question, index)}
                          type="date"
                          value={answer.value}
                          disabled={isSaving || isReadOnlyHistoryMode}
                          onChange={(event) => {
                            updateAnswer(question.questionId, {
                              value: event.target.value,
                              selectedOptionIds: [],
                            });
                          }}
                          onBlur={() => {
                            setQuestionTouched(question.questionId);
                          }}
                        />
                      </Field>
                    ) : null}

                    {!isSingleLineTextQuestion(question) &&
                    !isMultilineQuestion(question) &&
                    !isNumericQuestion(question) &&
                    !isDateQuestion(question) &&
                    !isRadioQuestion(question) &&
                    !isCheckboxQuestion(question) ? (
                      <Field>
                        <Label htmlFor={getQuestionInputId(question, index)}>
                          Answer
                        </Label>
                        <Input
                          id={getQuestionInputId(question, index)}
                          type="text"
                          value={answer.value}
                          disabled={isSaving || isReadOnlyHistoryMode}
                          onChange={(event) => {
                            updateAnswer(question.questionId, {
                              value: event.target.value,
                              selectedOptionIds: [],
                            });
                          }}
                          onBlur={() => {
                            setQuestionTouched(question.questionId);
                          }}
                        />
                      </Field>
                    ) : null}

                    {isRadioQuestion(question) ? (
                      <HiddenLabelChoiceList>
                        <ChoiceList>
                          <RadioGroup
                            label={question.questionTitle}
                            isDisabled={isSaving || isReadOnlyHistoryMode}
                            value={
                              answer.selectedOptionIds[0]
                                ? String(answer.selectedOptionIds[0])
                                : ''
                            }
                            onChange={(event) => {
                              if (isReadOnlyHistoryMode) {
                                return;
                              }

                              const selectedOptionId = Number(
                                event.target.value
                              );
                              if (!selectedOptionId) {
                                return;
                              }

                              updateAnswer(question.questionId, {
                                value: getOptionTitle(
                                  question,
                                  selectedOptionId
                                ),
                                selectedOptionIds: [selectedOptionId],
                              });
                              setQuestionTouched(question.questionId);
                            }}
                          >
                            <RadioGroup.Column>
                              {question.options.map((option) => {
                                return (
                                  <RadioGroup.Radio
                                    key={option.questionOptionId}
                                    label={option.optionTitle}
                                    value={String(option.questionOptionId)}
                                  />
                                );
                              })}
                            </RadioGroup.Column>
                          </RadioGroup>
                        </ChoiceList>
                      </HiddenLabelChoiceList>
                    ) : null}

                    {isCheckboxQuestion(question) ? (
                      <ChoiceList>
                        {question.options.map((option) => {
                          const isChecked = answer.selectedOptionIds.includes(
                            option.questionOptionId
                          );
                          return (
                            <Checkbox
                              key={option.questionOptionId}
                              inputId={`SURVEY_PAGE.CHECKBOX_${index + 1}_${option.orderId}`}
                              label={option.optionTitle}
                              isChecked={isChecked}
                              isDisabled={isSaving || isReadOnlyHistoryMode}
                              onChange={() => {
                                const nextSelectedOptionIds = isChecked
                                  ? answer.selectedOptionIds.filter(
                                      (selectedOptionId) => {
                                        return (
                                          selectedOptionId !==
                                          option.questionOptionId
                                        );
                                      }
                                    )
                                  : [
                                      ...answer.selectedOptionIds,
                                      option.questionOptionId,
                                    ].sort((left, right) => {
                                      return left - right;
                                    });

                                updateAnswer(question.questionId, {
                                  value: nextSelectedOptionIds
                                    .map((questionOptionId) => {
                                      return getOptionTitle(
                                        question,
                                        questionOptionId
                                      );
                                    })
                                    .join(', '),
                                  selectedOptionIds: nextSelectedOptionIds,
                                });
                                setQuestionTouched(question.questionId);
                              }}
                            />
                          );
                        })}
                      </ChoiceList>
                    ) : null}

                    {validationMessage ? (
                      <Text color="$web.semantic.color.text.content.error">
                        {validationMessage}
                      </Text>
                    ) : null}
                  </QuestionCard>
                );
              })}
            </QuestionList>
          ) : null}

          {!isLoading &&
          entry !== null &&
          visibleQuestions.length > 0 &&
          !isReadOnlyHistoryMode ? (
            <ActionRow>
              <Button
                type="button"
                variant="filled"
                size="sm"
                onClick={() => {
                  void handleSave(false);
                }}
                isDisabled={isSaving}
              >
                Save Draft
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openPreview}
                isDisabled={
                  isSaving || requiredAnsweredCount < requiredVisibleCount
                }
              >
                Review And Submit
              </Button>
              <Button
                type="button"
                variant="text"
                size="sm"
                onClick={() => {
                  returnToHome('cancel');
                }}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
            </ActionRow>
          ) : null}

          {!isLoading &&
          entry !== null &&
          visibleQuestions.length > 0 &&
          isReadOnlyHistoryMode ? (
            <ActionRow>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={returnToHistory}
                isDisabled={isSaving}
              >
                Back to History
              </Button>
            </ActionRow>
          ) : null}

          {actionMessage ? <Text>{actionMessage}</Text> : null}

          {isSaving && (
            <PageSpinner label="Submitting survey response" blocking />
          )}

          <SharedDialog
            isOpen={showPreview}
            onClose={() => {
              setShowPreview(false);
              setAttested(false);
            }}
            title="Review your answers"
            description="Confirm the answers below, complete attestation, and submit the survey."
            size="lg"
            primaryAction={{
              label: 'Submit Survey',
              onClick: () => {
                void handleSave(true);
              },
              disabled: isSaving || !attested,
              loading: isSaving,
            }}
            secondaryAction={{
              label: 'Close',
              onClick: () => {
                setShowPreview(false);
                setAttested(false);
              },
              variant: 'outline',
              disabled: isSaving,
            }}
          >
            <PreviewList>
              <V1Table
                title="Survey answers"
                highlightRowOnHover
                columns={[
                  { key: 'question', name: 'Question' },
                  { key: 'answer', name: 'Your Answer' },
                ]}
                rows={visibleQuestions.map((question) => {
                  return {
                    id: question.questionId,
                    question: question.questionTitle,
                    answer: getAnswerText(
                      question,
                      answers[question.questionId]
                    ),
                  };
                })}
              />
              <Divider margin="0" />
              <AttestationBox>
                <Checkbox
                  inputId="SURVEY_PAGE.ATTESTATION_1"
                  label="I attest that the information in this survey is accurate."
                  isChecked={attested}
                  isDisabled={isSaving}
                  onChange={(event) => {
                    setAttested(event.target.checked);
                    if (event.target.checked) {
                      setError(null);
                    }
                  }}
                />
              </AttestationBox>
              {error ? (
                <Alert
                  status="error"
                  title="Attestation required"
                  dismissible={false}
                >
                  {error}
                </Alert>
              ) : null}
            </PreviewList>
          </SharedDialog>

          <SharedDialog
            isOpen={showSaveSuccessDialog}
            onClose={() => {
              return;
            }}
            title={
              saveSuccessWasFinalSubmit
                ? 'Survey submitted successfully'
                : 'Draft saved successfully'
            }
            description={buildSaveSuccessDescription(saveSuccessWasFinalSubmit)}
            dismissible={false}
            primaryAction={{
              label: 'OK',
              onClick: () => {
                setShowSaveSuccessDialog(false);
                setSaveSuccessResponse(null);
                returnToHome('save');
              },
              disabled: isSaving,
            }}
          >
            {saveSuccessWasFinalSubmit ? (
              <V1Table
                title="Submission summary"
                columns={[
                  { key: 'label', name: 'Detail' },
                  { key: 'value', name: 'Value' },
                ]}
                rows={[
                  {
                    id: 'started',
                    label: 'Date started',
                    value: formatDisplayDateTime(
                      saveSuccessResponse?.startDate,
                      'Not available'
                    ),
                  },
                  {
                    id: 'completed',
                    label: 'Date completed',
                    value: formatDisplayDateTime(
                      saveSuccessResponse?.completedDate,
                      'Not available'
                    ),
                  },
                ]}
              />
            ) : null}
          </SharedDialog>
        </Page>
      </SurveyLayout>
    </React.Fragment>
  );
};
